/**
 * Tells you what the map is about to do while a draw tool is armed.
 *
 * The old flow gave no feedback at all: you clicked an unlabelled circle in the
 * toolbar, and nothing on screen changed. Whether the next click would drop a
 * waypoint or pan the map was something you found out by trying it. Worse, one
 * click placed a pin and TerraDraw silently dropped back to select mode, so the
 * second pin people expected never appeared.
 *
 * This says what the next click does and how to get out.
 *
 * Only the point tool has copy here. Select is still a registered TerraDraw
 * mode - the popup's Move button needs one, or selectFeature throws - but its
 * toolbar button is hidden, so a user can no longer enter select mode
 * directly, and the Move flow drives its own prompt from the popup.
 */

import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot, faXmark } from '@fortawesome/free-solid-svg-icons';

const MODE_COPY = {
  point: {
    icon: faLocationDot,
    title: 'Click the map to drop a waypoint',
    hint: 'Esc to stop',
  },
};

const s = {
  wrap: {
    position: 'absolute',
    top: 16,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    maxWidth: 'calc(100vw - 32px)',
    padding: '9px 12px',
    borderRadius: 10,
    background: 'rgba(31, 35, 39, 0.94)',
    color: '#FFFFFF',
    boxShadow: '0 4px 16px rgba(0,0,0,0.28)',
    fontFamily: 'var(--gf-font-sans, "Poppins", sans-serif)',
    fontSize: 13,
    lineHeight: 1.3,
    pointerEvents: 'auto',
  },
  icon: { color: 'var(--gf-primary-400, #FD7218)', flex: 'none' },
  hint: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.6)',
    borderLeft: '1px solid rgba(255,255,255,0.22)',
    paddingLeft: 10,
    flex: 'none',
  },
  close: {
    border: 0, background: 'none', cursor: 'pointer', padding: '0 0 0 2px',
    color: 'rgba(255,255,255,0.6)', flex: 'none',
  },
  toast: { background: 'rgba(31, 35, 39, 0.94)' },
};

export function WaypointHint({ mode, saved, onCancel }) {
  const [showSaved, setShowSaved] = useState(false);

  // "Waypoint saved" is the confirmation that the pin actually persisted to
  // Firestore, not just that a dot appeared. It clears itself.
  useEffect(() => {
    if (!saved) return undefined;
    setShowSaved(true);
    const t = setTimeout(() => setShowSaved(false), 2200);
    return () => clearTimeout(t);
  }, [saved]);

  if (showSaved) {
    return (
      <div style={s.wrap} role="status">
        <FontAwesomeIcon icon={faLocationDot} style={s.icon} />
        <span>Waypoint saved</span>
      </div>
    );
  }

  const copy = MODE_COPY[mode];
  if (!copy) return null;

  return (
    <div style={s.wrap} role="status">
      <FontAwesomeIcon icon={copy.icon} style={s.icon} />
      <span>{copy.title}</span>
      <span style={s.hint}>{copy.hint}</span>
      <button type="button" style={s.close} onClick={onCancel} aria-label="Stop placing waypoints">
        <FontAwesomeIcon icon={faXmark} size="sm" />
      </button>
    </div>
  );
}

export default WaypointHint;
